import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  IconButton,
  Link,
  Chip,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const primaryButtonSx = {
  bgcolor: ui.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '10px',
  px: 3,
  py: 1.1,
  boxShadow: 'none',
  '&:hover': { bgcolor: ui.primaryDark, boxShadow: 'none' },
};

const dialogPaperSx = {
  borderRadius: '16px',
  m: { xs: 2, sm: 4 },
  width: '100%',
};

const DialogHeader = ({ title, onClose }) => (
  <DialogTitle
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      fontWeight: 700,
      color: ui.text,
      pb: 1.5,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
      }}
    >
      <WorkspacePremiumOutlinedIcon fontSize="small" />
    </Box>
    <Box sx={{ flexGrow: 1 }}>{title}</Box>
    <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: ui.muted }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
);

const Certificate = ({ certificates, setCertificates, onDeleteCertificate }) => {
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const initialCertificateState = {
    name: '',
    organization: '',
    issueDate: null,
    expiryDate: null,
    url: '',
    comments: '',
    mediaFilePath: '',
  };

  const [newCertificate, setNewCertificate] = useState(initialCertificateState);

  // Resets the form to initial state
  const resetForm = () => {
    setNewCertificate(initialCertificateState);
  };

  // Opens edit modal with certificate data
  const handleEditCertificate = (index) => {
    setEditingIndex(index);
    setEditingCertificate({
      ...certificates[index],
      issueDate: certificates[index].issueDate ? dayjs(certificates[index].issueDate) : null,
      expiryDate: certificates[index].expiryDate ? dayjs(certificates[index].expiryDate) : null,
    });
    setShowEditModal(true);
  };

  // Saves edited certificate
  const handleSaveEdit = () => {
    const isIssueDateValid = editingCertificate?.issueDate && dayjs(editingCertificate.issueDate).isValid();
    if (!editingCertificate?.name || !editingCertificate?.organization || !isIssueDateValid) {
      alert('Please fill in all required fields: Name, Organization, and Issue Date.');
      return;
    }

    const updatedCertificate = {
      ...editingCertificate,
      issueDate: editingCertificate.issueDate ? dayjs(editingCertificate.issueDate).format('YYYY-MM-DD') : null,
      expiryDate: editingCertificate.expiryDate ? dayjs(editingCertificate.expiryDate).format('YYYY-MM-DD') : null,
    };

    setCertificates({
      action: 'update',
      certificate: updatedCertificate,
      index: editingIndex,
    });

    setShowEditModal(false);
    setEditingCertificate(null);
    setEditingIndex(null);
  };

  // Deletes a certificate by ID
  const handleDeleteCertificate = (certificateId) => {
    onDeleteCertificate(certificateId);
  };

  // Adds a new certificate
  const handleAddCertificate = () => {
    const isIssueDateValid = newCertificate.issueDate && dayjs(newCertificate.issueDate).isValid();
    if (!newCertificate.name || !newCertificate.organization || !isIssueDateValid) {
      alert('Please fill in all required fields: Name, Organization, and Issue Date.');
      return;
    }

    const formattedCertificate = {
      id: crypto.randomUUID(),
      name: newCertificate.name,
      organization: newCertificate.organization,
      issueDate: newCertificate.issueDate ? dayjs(newCertificate.issueDate).format('YYYY-MM-DD') : null,
      expiryDate: newCertificate.expiryDate ? dayjs(newCertificate.expiryDate).format('YYYY-MM-DD') : null,
      url: newCertificate.url || '',
      comments: newCertificate.comments || '',
      mediaFilePath: newCertificate.mediaFilePath || '',
    };

    setCertificates({
      action: 'add',
      certificate: formattedCertificate,
    });

    resetForm();
    setShowCertificateModal(false);
  };

  const safeCertificates = Array.isArray(certificates) ? certificates : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          mb: 3,
          borderRadius: '16px',
          border: `1px solid ${ui.border}`,
          backgroundColor: '#fff',
          boxShadow: ui.shadow,
          p: { xs: 2.5, sm: 3.5 },
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: ui.primarySoft,
                color: ui.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <WorkspacePremiumOutlinedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <Link
                href="/certificate"
                sx={{
                  color: ui.text,
                  '&:hover': { color: ui.primary },
                  textDecoration: 'none',
                }}
              >
                Certificates
              </Link>
            </Typography>
            <Chip
              size="small"
              label={safeCertificates.length}
              sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: ui.primarySoft, color: ui.primaryDark }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCertificateModal(true)}
            sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
          >
            Add Certificate
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {safeCertificates.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 5,
                px: 2,
                borderRadius: '12px',
                border: `1px dashed ${ui.border}`,
                backgroundColor: ui.surface,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '50%',
                  backgroundColor: ui.primarySoft,
                  color: ui.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WorkspacePremiumOutlinedIcon />
              </Box>
              <Typography variant="body1" sx={{ color: ui.muted }}>
                No certificates added yet.
              </Typography>
            </Box>
          ) : (
            safeCertificates.map((certificate, index) => {
              const issueDateFormatted = certificate.issueDate && dayjs(certificate.issueDate).isValid()
                ? dayjs(certificate.issueDate).format('MM/YYYY')
                : 'N/A';
              const expiryDateFormatted = certificate.expiryDate && dayjs(certificate.expiryDate).isValid()
                ? dayjs(certificate.expiryDate).format('MM/YYYY')
                : null;

              return (
                <Box
                  key={certificate.id}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '14px',
                    border: `1px solid ${ui.border}`,
                    borderLeft: `5px solid ${ui.primary}`,
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(16, 24, 40, 0.08)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: ui.text, fontSize: '1.05rem', wordBreak: 'break-word' }}>
                      {certificate.name || 'Unnamed Certificate'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word' }}>
                      {certificate.organization || 'Unknown Organization'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Chip
                        size="small"
                        icon={<EventOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
                        label={`Issued: ${issueDateFormatted}`}
                        sx={{ fontWeight: 600, borderRadius: '8px', backgroundColor: ui.surface, border: `1px solid ${ui.border}` }}
                      />
                      {expiryDateFormatted && (
                        <Chip
                          size="small"
                          icon={<EventOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
                          label={`Expires: ${expiryDateFormatted}`}
                          sx={{ fontWeight: 600, borderRadius: '8px', backgroundColor: ui.surface, border: `1px solid ${ui.border}` }}
                        />
                      )}
                    </Box>
                    {certificate.url && (
                      <Link
                        href={certificate.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 1.25,
                          fontWeight: 600,
                          color: ui.primary,
                          '&:hover': { color: ui.primaryDark },
                          textDecoration: 'none',
                        }}
                      >
                        View Certificate
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                      </Link>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlinedIcon fontSize="small" />}
                      onClick={() => handleEditCertificate(index)}
                      sx={{
                        flex: { xs: 1, sm: 'none' },
                        color: ui.primary,
                        borderColor: ui.primary,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        '&:hover': { borderColor: ui.primaryDark, backgroundColor: ui.primarySoft },
                      }}
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete">
                      <Button
                        variant="outlined"
                        aria-label="Delete certificate"
                        onClick={() => handleDeleteCertificate(certificate.id)}
                        sx={{
                          minWidth: 44,
                          px: 1.25,
                          color: ui.danger,
                          borderColor: '#f5c2c7',
                          borderRadius: '10px',
                          '&:hover': { borderColor: ui.danger, backgroundColor: ui.dangerSoft },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* Add certificate dialog */}
      <Dialog
        open={showCertificateModal}
        onClose={() => setShowCertificateModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader title="Add Certificate" onClose={() => setShowCertificateModal(false)} />
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              mt: 0.5,
            }}
          >
            <TextField
              id="name"
              label="Name"
              fullWidth
              value={newCertificate.name}
              onChange={(e) => setNewCertificate({ ...newCertificate, name: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <TextField
              id="organization"
              label="Organization"
              fullWidth
              value={newCertificate.organization}
              onChange={(e) => setNewCertificate({ ...newCertificate, organization: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <DatePicker
              label="Issue Date"
              views={['year', 'month', 'day']}
              format="YYYY-MM-DD"
              value={newCertificate.issueDate}
              onChange={(newValue) => setNewCertificate({ ...newCertificate, issueDate: newValue ? dayjs(newValue) : null })}
              slotProps={{ textField: { required: true, fullWidth: true, sx: fieldSx } }}
            />
            <DatePicker
              label="Expiry Date"
              views={['year', 'month', 'day']}
              format="YYYY-MM-DD"
              value={newCertificate.expiryDate}
              onChange={(newValue) => setNewCertificate({ ...newCertificate, expiryDate: newValue ? dayjs(newValue) : null })}
              slotProps={{ textField: { fullWidth: true, sx: fieldSx } }}
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="URL"
                fullWidth
                type="url"
                value={newCertificate.url}
                onChange={(e) => setNewCertificate({ ...newCertificate, url: e.target.value })}
                placeholder="https://example.com/certificate"
                variant="outlined"
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="Comments"
                multiline
                minRows={3}
                fullWidth
                value={newCertificate.comments}
                onChange={(e) => setNewCertificate({ ...newCertificate, comments: e.target.value })}
                inputProps={{ maxLength: 500 }}
                helperText={`${newCertificate.comments.length}/500 characters`}
                sx={fieldSx}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setShowCertificateModal(false)}
            sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddCertificate} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit certificate dialog */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader title="Edit Certificate" onClose={() => setShowEditModal(false)} />
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              mt: 0.5,
            }}
          >
            <TextField
              id="edit-name"
              label="Name"
              fullWidth
              value={editingCertificate?.name || ''}
              onChange={(e) => setEditingCertificate({ ...editingCertificate, name: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <TextField
              id="edit-organization"
              label="Organization"
              fullWidth
              value={editingCertificate?.organization || ''}
              onChange={(e) => setEditingCertificate({ ...editingCertificate, organization: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <DatePicker
              label="Issue Date"
              views={['year', 'month', 'day']}
              format="YYYY-MM-DD"
              value={editingCertificate?.issueDate || null}
              onChange={(newValue) => setEditingCertificate({ ...editingCertificate, issueDate: newValue ? dayjs(newValue) : null })}
              slotProps={{ textField: { required: true, fullWidth: true, sx: fieldSx } }}
            />
            <DatePicker
              label="Expiry Date"
              views={['year', 'month', 'day']}
              format="YYYY-MM-DD"
              value={editingCertificate?.expiryDate || null}
              onChange={(newValue) => setEditingCertificate({ ...editingCertificate, expiryDate: newValue ? dayjs(newValue) : null })}
              slotProps={{ textField: { fullWidth: true, sx: fieldSx } }}
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                id="edit-url"
                label="URL"
                fullWidth
                type="url"
                value={editingCertificate?.url || ''}
                onChange={(e) => setEditingCertificate({ ...editingCertificate, url: e.target.value })}
                placeholder="https://example.com/certificate"
                variant="outlined"
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="Comments"
                multiline
                minRows={3}
                fullWidth
                value={editingCertificate?.comments || ''}
                onChange={(e) => setEditingCertificate({ ...editingCertificate, comments: e.target.value })}
                inputProps={{ maxLength: 500 }}
                helperText={`${editingCertificate?.comments?.length || 0}/500 characters`}
                sx={fieldSx}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setShowEditModal(false)}
            sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} sx={primaryButtonSx}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Certificate;